import {
  EnvironmentOutlined,
  InfoCircleOutlined,
  UserOutlined
} from '@ant-design/icons';
import { Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React from 'react';
import { AreaLeaderboardItem } from '../../../../types/leaderboard';
import { formatNumber, formatRupiah, glassTableContainerStyle } from '../../utils';

const { Title, Text } = Typography;

interface AreaLeaderboardTableProps {
  dataSource: AreaLeaderboardItem[];
  loading: boolean;
  sortBy?: 'omzet' | 'ok' | 'rsv' | 'name';
  sortOrder?: 'desc' | 'asc';
  onTableChange: (pagination: any, filters: any, sorter: any) => void;
}

export const AreaLeaderboardTable: React.FC<AreaLeaderboardTableProps> = ({
  dataSource,
  loading,
  sortBy = 'omzet',
  sortOrder = 'desc',
  onTableChange,
}) => {
  const getSortingLabel = () => {
    let colName = 'Omzet Closing';
    if (sortBy === 'name') colName = 'Nama Kawasan (A-Z)';
    if (sortBy === 'ok') colName = 'Jumlah Closing (OK)';
    if (sortBy === 'rsv') colName = 'Jumlah Reservasi (RSV)';

    const dir = sortOrder === 'asc' ? 'Terendah' : 'Tertinggi';
    return `${colName} (${dir})`;
  };

  const getAntdSortOrder = (key: string) => {
    if (sortBy === key) {
      return sortOrder === 'asc' ? ('ascend' as const) : ('descend' as const);
    }
    return null;
  };

  const columns: ColumnsType<AreaLeaderboardItem> = [
    {
      title: 'Rank',
      dataIndex: 'rank',
      key: 'rank',
      width: 75,
      align: 'center',
      fixed: 'left',
      render: (rank: number) => {
        if (!rank || rank <= 0) {
          return (
            <Text type="secondary" style={{ fontSize: '13px', fontWeight: 500 }}>
              -
            </Text>
          );
        }
        if (rank === 1) {
          return (
            <Tag color="gold" style={{ borderRadius: '4px', fontWeight: 700, padding: '1px 8px', margin: 0 }}>
              #1
            </Tag>
          );
        }
        if (rank === 2) {
          return (
            <Tag style={{ borderRadius: '4px', fontWeight: 600, color: '#475569', background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '1px 8px', margin: 0 }}>
              #2
            </Tag>
          );
        }
        if (rank === 3) {
          return (
            <Tag style={{ borderRadius: '4px', fontWeight: 600, color: '#92400e', background: '#fef3c7', border: '1px solid #fde68a', padding: '1px 8px', margin: 0 }}>
              #3
            </Tag>
          );
        }
        return (
          <Text type="secondary" style={{ fontSize: '13px', fontWeight: 500 }}>
            #{rank}
          </Text>
        );
      },
    },
    {
      title: (
        <Text strong style={{ color: sortBy === 'name' ? '#1890ff' : 'inherit' }}>
          Nama Kawasan Properti
        </Text>
      ),
      dataIndex: 'property_area_name',
      key: 'property_area_name',
      width: 240,
      fixed: 'left',
      sorter: true,
      sortOrder: getAntdSortOrder('name'),
      render: (name: string, record) => (
        <Space size={8} align="center">
          <EnvironmentOutlined style={{ color: '#7BAA18', fontSize: '15px' }} />
          <Text strong style={{ color: '#0f172a', fontSize: '13px' }}>
            {name}
          </Text>
          {record.is_my_area && (
            <Tag
              color="blue"
              style={{
                marginLeft: '4px',
                borderRadius: '4px',
                fontSize: '11px',
                padding: '0 6px',
              }}
            >
              Kawasan Saya
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: (
        <Text strong style={{ color: sortBy === 'omzet' ? '#1890ff' : 'inherit' }}>
          Omzet Closing
        </Text>
      ),
      dataIndex: 'omzet',
      key: 'omzet',
      width: 190,
      sorter: true,
      sortOrder: getAntdSortOrder('omzet'),
      align: 'right',
      render: (val: number, record) => (
        <Text strong style={{ color: '#0f172a', fontSize: '13px' }}>
          {record.omzet_rupiah || formatRupiah(val)}
        </Text>
      ),
    },
    {
      title: (
        <Text strong style={{ color: sortBy === 'ok' ? '#1890ff' : 'inherit' }}>
          Closing (OK)
        </Text>
      ),
      dataIndex: 'ok_count',
      key: 'ok',
      width: 130,
      sorter: true,
      sortOrder: getAntdSortOrder('ok'),
      align: 'center',
      render: (count: number) => (
        <Text strong style={{ color: '#0f172a' }}>
          {formatNumber(count)} Unit
        </Text>
      ),
    },
    {
      title: (
        <Text strong style={{ color: sortBy === 'rsv' ? '#1890ff' : 'inherit' }}>
          Reservasi (RSV)
        </Text>
      ),
      dataIndex: 'rsv_count',
      key: 'rsv',
      width: 140,
      sorter: true,
      sortOrder: getAntdSortOrder('rsv'),
      align: 'center',
      render: (count: number) => (
        <Text style={{ color: '#334155' }}>
          {formatNumber(count)}
        </Text>
      ),
    },
    {
      title: 'Total Leads',
      dataIndex: 'total_leads',
      key: 'total_leads',
      width: 120,
      align: 'center',
      render: (count: number) => (
        <Text style={{ color: '#334155' }}>
          {formatNumber(count || 0)}
        </Text>
      ),
    },
    {
      title: 'Total Sales',
      dataIndex: 'total_sales',
      key: 'total_sales',
      width: 110,
      align: 'center',
      render: (count: number) => (
        <Text style={{ color: '#334155' }}>
          {count || 0} Agen
        </Text>
      ),
    },
    {
      title: 'Top Sales Kawasan',
      dataIndex: 'top_sales_name',
      key: 'top_sales_name',
      width: 180,
      render: (name?: string) => {
        if (!name) {
          return <Text type="secondary" italic>-</Text>;
        }
        return (
          <Space size={6}>
            <UserOutlined style={{ color: '#64748b' }} />
            <Text strong style={{ color: '#0f172a' }}>
              {name}
            </Text>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={glassTableContainerStyle} className="area-leaderboard-container">
      <style>{`
        .area-leaderboard-container .ant-table-thead > tr > th {
          background: rgba(248, 250, 252, 0.85) !important;
          border-bottom: 1px solid rgba(203, 213, 225, 0.8) !important;
          font-weight: 700;
        }
        .area-leaderboard-container .ant-table-tbody > tr:hover > td {
          background: rgba(241, 245, 249, 0.7) !important;
        }
        .area-leaderboard-my-row {
          background: rgba(24, 144, 255, 0.03) !important;
        }
      `}</style>

      {/* Header Controls */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <Space size={6} direction="vertical">
          <Space size={6}>
            <Title level={4} style={{ margin: 0, color: '#1e293b', fontWeight: 700 }}>
              Peringkat Lengkap Kawasan Properti
            </Title>
            <Tooltip title="Tabel perbandingan performa komprehensif seluruh kawasan operasional untuk evaluasi dan benchmarking sehat.">
              <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '13px', cursor: 'pointer' }} />
            </Tooltip>
          </Space>
          <Text type="secondary" style={{ fontSize: '12px', display: 'block', margin: 0 }}>
            Urutan aktif: <Text strong style={{ color: '#1890ff' }}>{getSortingLabel()}</Text>
          </Text>
        </Space>

        <Text type="secondary" style={{ fontSize: '13px' }}>
          Total: <Text strong>{dataSource?.length || 0}</Text> Kawasan
        </Text>
      </div>

      <Table<AreaLeaderboardItem>
        dataSource={dataSource}
        columns={columns}
        rowKey="property_area_id"
        loading={loading}
        scroll={{ x: 1050 }}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (totalCount) => `Total ${totalCount} kawasan`,
        }}
        onChange={onTableChange}
        rowClassName={(record) => (record.is_my_area ? 'area-leaderboard-my-row' : '')}
      />
    </div>
  );
};

export default AreaLeaderboardTable;
